import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import {
    MultiStepBriefForm,
    useBriefContext,
    useBriefQuery,
    useUpdateProductOnBriefChange,
} from '@99designs/feature-design-services-brief';
import { getApolloErrorType } from '@99designs/graph-utils';
import { __, getCurrentCulture } from '@99designs/i18n';
import { useSendPageViewOnce } from '@99designs/tracking';
import { useBriefRedirects } from '../../hooks';
import { ErrorPage, NotFoundErrorPage, PermissionErrorPage } from '../Error';

export const Brief: React.FC = () => {
    const { jobId } = useParams();
    const briefId = jobId as string;
    const locale = useMemo(() => getCurrentCulture(), []);

    useSendPageViewOnce(locale);
    const { SignIn, isSignedIn } = useIdentityContext();
    const { onCompleted, isRequestQueryLoading } = useBriefContext();

    const { data, loading, error } = useBriefQuery({
        variables: { id: briefId },
        onCompleted,
        errorPolicy: 'all',
    });

    useUpdateProductOnBriefChange(data?.brief);

    const { customerIdentifiedError, isRedirecting } = useBriefRedirects({
        correlationId: data?.brief?.correlationId,
        briefState: data?.brief?.state,
        currentUrl: data?.brief?.request?.facts?.currentUrl,
    });

    if (loading || isRequestQueryLoading || !data || isRedirecting) {
        return <FullPageSpinner />;
    }

    if (customerIdentifiedError) {
        return (
            <ErrorPage
                title={__(
                    'There was an error saving your brief. Please refresh the page or try again later.',
                    {
                        _context: 'Shown to user if there is an error saving the brief',
                    }
                )}
                error={
                    new Error(
                        `Could not claim brief ${briefId} before redirecting to dashboard: ${customerIdentifiedError?.message}`
                    )
                }
            />
        );
    }

    if (error) {
        const errorType = getApolloErrorType(error);
        if (errorType === 'UNAUTHENTICATED') {
            if (!isSignedIn) {
                SignIn();
                return <FullPageSpinner />;
            }

            return (
                <ErrorPage
                    title={__(
                        'There was an error loading your brief. Please refresh the page or try again later.'
                    )}
                    error={
                        new Error(
                            `User is logged in on Vistaprint but has no 99designs session: ${error.message}`
                        )
                    }
                    metadata={{ briefId }}
                />
            );
        }

        if (errorType === 'PERMISSION_DENIED') {
            return <PermissionErrorPage />;
        }

        if (errorType === 'NOT_FOUND') {
            return <NotFoundErrorPage />;
        }

        if (errorType === 'UNKNOWN_ERROR') {
            return (
                <ErrorPage
                    error={new Error(`Unknown error loading brief: ${error.message}`)}
                    metadata={{ briefId }}
                />
            );
        }

        // Ignore 'no product found for mpvID' as mpvID can be empty or incorrect without causing an actual error.
        // ToDo: Remove this check once confident the error no longer occurs.
        if (!error.message.includes('no product found for mpvID')) {
            return <ErrorPage error={error} />;
        }
    }

    return (
        <>
            <Helmet>
                <title>{__('Design Services Brief')} | Vistaprint</title>
            </Helmet>
            <MultiStepBriefForm brief={data.brief} locale={locale} />
        </>
    );
};
